/**
 * Which of the two production hostnames the app is being served from.
 *
 * `session.ts` answers *who* is signed in; this answers *where* the browser
 * is — the marketing site (`blackarrow.com.pk`) or the portal
 * (`app.blackarrow.com.pk`). Both hostnames come from env vars set only in
 * the production build (see infra/deploy-web.sh); unset in dev, `vite
 * preview`, and any other host, both checks below return false and every
 * caller falls through to pre-split behaviour. That is the invariant that
 * keeps localhost unrestricted.
 */

// Read on every call, not cached at module load: a build only ever sees one
// value for the lifetime of the page, but the test suite stubs these per
// case, and a module-level const would freeze in whatever was set at the
// first import.
function appHost(): string | undefined {
    return import.meta.env.VITE_APP_HOST;
}

function siteHost(): string | undefined {
    return import.meta.env.VITE_SITE_HOST;
}

export function isPortalHost(): boolean {
    const host = appHost();

    return host !== undefined && window.location.hostname === host;
}

export function isSiteHost(): boolean {
    const host = siteHost();

    return (
        host !== undefined &&
        (window.location.hostname === host || window.location.hostname === `www.${host}`)
    );
}

/**
 * An absolute URL to `path` on the portal host.
 *
 * Only ever called from a branch that already checked `isSiteHost()`, so a
 * missing `VITE_APP_HOST` here is a build misconfiguration, not a runtime
 * case to degrade gracefully from — fail loudly instead of redirecting to
 * `https://undefined/...`.
 */
export function toPortal(path: string): string {
    const host = appHost();

    if (host === undefined) {
        throw new Error('toPortal() called without VITE_APP_HOST configured');
    }

    return `https://${host}${path}`;
}

/** An absolute URL to `path` on the marketing site host. See {@link toPortal}. */
export function toSite(path: string): string {
    const host = siteHost();

    if (host === undefined) {
        throw new Error('toSite() called without VITE_SITE_HOST configured');
    }

    return `https://${host}${path}`;
}
